import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
} from 'react-icons/fa';
import { Container } from '../ui';
import { CONFERENCE } from '../../data/conference';

const Wrap = styled.footer`
  border-top: 1px solid var(--line);
  background: var(--bg-elev);
  position: relative;
  overflow: hidden;
`;

const BigWordmark = styled.div`
  font-family: var(--font-display);
  font-weight: 800;
  /* wordmark measures ~12em in Syne 800 — 7.5vw keeps it inside any viewport */
  font-size: min(7.5vw, 8.5rem);
  line-height: 0.95;
  letter-spacing: -0.03em;
  text-align: center;
  padding: clamp(2.5rem, 6vw, 4.5rem) 1rem 0;
  user-select: none;
  background: linear-gradient(180deg, rgba(34, 31, 26, 0.16) 0%, rgba(34, 31, 26, 0.02) 90%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  white-space: nowrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 1.1fr 1fr;
  gap: clamp(2rem, 5vw, 4rem);
  padding: clamp(2.5rem, 6vw, 4rem) 0;
  border-top: 1px solid var(--line);
  margin-top: clamp(2rem, 5vw, 3.5rem);

  @media (max-width: 980px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Col = styled.div`
  h3 {
    font-family: var(--font-mono);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--copper);
    margin-bottom: 1.4rem;
  }

  p {
    color: var(--ink-dim);
    font-size: 0.95rem;
    line-height: 1.7;
    margin-bottom: 0.9rem;
  }

  strong {
    color: var(--ink);
    font-weight: 600;
  }
`;

const QuickLinks = styled.ul`
  list-style: none;

  li {
    margin-bottom: 0.7rem;
  }

  a {
    color: var(--ink-dim);
    font-size: 0.95rem;
    transition: color 0.2s, padding-left 0.25s var(--ease-out);

    &:hover {
      color: var(--copper-bright);
      padding-left: 6px;
    }
  }
`;

const ContactRow = styled.div`
  display: flex;
  gap: 0.9rem;
  margin-bottom: 1.1rem;
  color: var(--ink-dim);
  font-size: 0.95rem;
  line-height: 1.6;

  svg {
    color: var(--copper);
    flex: none;
    margin-top: 0.3rem;
    font-size: 0.9rem;
  }

  a {
    color: var(--ink-dim);
    transition: color 0.2s;

    &:hover {
      color: var(--copper-bright);
    }
  }
`;

const Socials = styled.div`
  display: flex;
  gap: 0.7rem;
  margin-top: 0.4rem;
`;

const SocialBtn = styled.a`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line-strong);
  border-radius: 50%;
  color: var(--ink-dim);
  font-size: 0.95rem;
  transition: all 0.25s var(--ease-out);

  &:hover {
    color: #16100a;
    background: var(--copper);
    border-color: var(--copper);
    transform: translateY(-3px);
  }
`;

const LogosRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.4rem;

  /* colored IEMENTech mark sits bare; IEM/UEM marks are white-on-transparent so they get a slim graphite tile */
  .bare {
    width: 60px;
    height: 60px;
    flex: none;
    object-fit: contain;
    transition: transform 0.25s var(--ease-out);
  }

  .bare:hover {
    transform: translateY(-3px);
  }

  .tile {
    width: 60px;
    height: 60px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: linear-gradient(160deg, #2c2f36, #16181d);
    border: 1px solid rgba(201, 117, 26, 0.4);
    box-shadow: 0 6px 16px -8px rgba(48, 42, 30, 0.45);
    transition: transform 0.25s var(--ease-out), border-color 0.25s var(--ease-out);
  }

  .tile:hover {
    transform: translateY(-3px);
    border-color: rgba(201, 117, 26, 0.75);
  }

  .tile img {
    width: 44px;
    height: 44px;
    object-fit: contain;
  }
`;

const Bottom = styled.div`
  border-top: 1px solid var(--line);
  padding: 1.6rem 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;

  p {
    font-family: var(--font-mono);
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
  }

  a {
    color: var(--copper);
    transition: color 0.2s;

    &:hover {
      color: var(--copper-bright);
    }
  }
`;

const quickLinks = [
  { to: '/registration', label: 'Registration' },
  { to: '/submissions', label: 'Call for Papers' },
  { to: '/key-dates', label: 'Key Dates' },
  { to: '/topics', label: 'Conference Topics' },
  { to: '/committee', label: 'Committee' },
  { to: '/speakers', label: 'Speakers' },
];

export default function Footer() {
  return (
    <Wrap>
      <BigWordmark aria-hidden="true">IEMENTech '27</BigWordmark>
      <Container>
        <Grid>
          <Col>
            <h3>The Conference</h3>
            <LogosRow>
              <img className="bare" src="/assets/logo2.png" alt="IEMENTech logo" />
              <span className="tile">
                <img src="/assets/IEM_Logo-150x150.png" alt="IEM Kolkata logo" />
              </span>
              <span className="tile">
                <img src="/assets/UEM_Logo-150x150.png" alt="UEM logo" />
              </span>
            </LogosRow>
            <p>{CONFERENCE.fullName}</p>
            <p>
              Organized by Dept. of ECE, Institute of Engineering &amp; Management, Kolkata
            </p>
            <p>
              <strong>Date:</strong> {CONFERENCE.dateShort}
              <br />
              <strong>Venue:</strong> {CONFERENCE.venue}
            </p>
          </Col>

          <Col>
            <h3>Quick Links</h3>
            <QuickLinks>
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to}>{l.label}</Link>
                </li>
              ))}
            </QuickLinks>
          </Col>

          <Col>
            <h3>Contact</h3>
            <ContactRow>
              <FaMapMarkerAlt aria-hidden="true" />
              <span>
                Indian Institute of Technology Guwahati,
                <br />
                North Guwahati, Guwahati, Assam, India
              </span>
            </ContactRow>
            <ContactRow>
              <FaPhoneAlt aria-hidden="true" />
              <span>
                {CONFERENCE.phones[0]}
                <br />
                {CONFERENCE.phones[1]}
              </span>
            </ContactRow>
            <ContactRow>
              <FaEnvelope aria-hidden="true" />
              <a href={`mailto:${CONFERENCE.email}`}>{CONFERENCE.email}</a>
            </ContactRow>
          </Col>

          <Col>
            <h3>Follow Us</h3>
            <p>Stay connected for updates and announcements</p>
            <Socials>
              <SocialBtn href="#" aria-label="Facebook">
                <FaFacebookF />
              </SocialBtn>
              <SocialBtn href="#" aria-label="Twitter / X">
                <FaTwitter />
              </SocialBtn>
              <SocialBtn href="#" aria-label="LinkedIn">
                <FaLinkedinIn />
              </SocialBtn>
              <SocialBtn href="#" aria-label="Instagram">
                <FaInstagram />
              </SocialBtn>
            </Socials>
            <p style={{ marginTop: '1.6rem' }}>
              <strong>Technical Co-Sponsor:</strong>
              <br />
              {CONFERENCE.coSponsor}
            </p>
          </Col>
        </Grid>

        <Bottom>
          <p>
            © {CONFERENCE.year} IEMENTech. All rights reserved. | Institute of Engineering &amp;
            Management, Kolkata
          </p>
          <p>
            Site made by{' '}
            <a
              href="https://www.linkedin.com/in/sumitkumardas-ai/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Sumit Kumar Das
            </a>
          </p>
        </Bottom>
      </Container>
    </Wrap>
  );
}
